import { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa6';
import { PDFGenerator } from '../../../shared/components/PDF';

interface TrainingExecutionProps {
    isTrainingModels: boolean;
    isGeneratingDatasets: boolean;
    isUploading: boolean;
    trainingStatus: string | null;
    uploadSessionId: string | null;
    hasTrainedModel: boolean;
    onTrainAndValidate: () => void;
    onSaveModel: () => Promise<void> | void;
}

const TrainingExecution = memo(({
    isTrainingModels,
    isGeneratingDatasets,
    isUploading,
    trainingStatus,
    uploadSessionId,
    hasTrainedModel,
    onTrainAndValidate,
    onSaveModel,
}: TrainingExecutionProps) => {
    const { t } = useTranslation('training');
    const [isSavingModel, setIsSavingModel] = useState(false);

    const handleSaveModelClick = useCallback(async () => {
        setIsSavingModel(true);
        try {
            await onSaveModel();
        } finally {
            setIsSavingModel(false);
        }
    }, [onSaveModel]);

    const isDisabled = isUploading || isGeneratingDatasets || isTrainingModels || trainingStatus === 'running';
    const isTraining = isTrainingModels || trainingStatus === 'running';

    return (
        <div className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <button
                type="button"
                className="inline-flex items-center gap-1.5 sm:gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-150"
                onClick={onTrainAndValidate}
                disabled={isDisabled}
            >
                {isTraining ? (
                    <>
                        <FaSpinner className="animate-spin text-xs" />
                        <span>{t('training.buttons.training')}</span>
                    </>
                ) : (
                    <span>{t('training.buttons.trainAndValidate')}</span>
                )}
            </button>

            {/* PDF Generator */}
            {uploadSessionId && (
                <PDFGenerator
                    sessionId={uploadSessionId}
                    className="inline-flex items-center gap-1.5 sm:gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-800 active:scale-[0.98] transition-all duration-150"
                />
            )}

            {/* Save Model button - only show after training completes */}
            {hasTrainedModel && (
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 sm:gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-150"
                    onClick={handleSaveModelClick}
                    disabled={isSavingModel || isUploading || isGeneratingDatasets || isTrainingModels || !uploadSessionId}
                >
                    {isSavingModel ? (
                        <>
                            <FaSpinner className="animate-spin text-xs" />
                            <span>{t('training.buttons.loadingModel')}</span>
                        </>
                    ) : (
                        <span>{t('training.buttons.saveModel')}</span>
                    )}
                </button>
            )}
        </div>
    );
});

TrainingExecution.displayName = 'TrainingExecution';

export default TrainingExecution;
